
import React, { Suspense, lazy, useState, useEffect } from 'react';
import { Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { ViewState } from './types';
import { APP_URL } from './constants';
import LanguagePicker from './components/LanguagePicker';
import { AppLanguage, LANGUAGE_STORAGE_KEY, LANGUAGE_PICKED_KEY } from './utils/i18n';

const MarketingHome = lazy(() => import('./components/MarketingHome'));
const AboutPage = lazy(() => import('./components/AboutPage'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const SubscriptionTerms = lazy(() => import('./components/SubscriptionTerms'));
const MatchArchive = lazy(() => import('./components/MatchArchive'));
const MatchViewer = lazy(() => import('./components/MatchViewer'));
const AccountDeletion = lazy(() => import('./components/AccountDeletion'));
const UnsubscribeView = lazy(() => import('./components/UnsubscribeView'));
const BasketballPlaysPage = lazy(() => import('./components/BasketballPlaysPage'));
const PlayCategoryPage = lazy(() => import('./components/PlayCategoryPage'));
const PlayDetailPage = lazy(() => import('./components/PlayDetailPage'));
const CoachBoardPage = lazy(() => import('./components/CoachBoardPage'));
const AICoachPage = lazy(() => import('./components/AICoachPage'));
const PlaybookCreatorPage = lazy(() => import('./components/PlaybookCreatorPage'));
const BlogPage = lazy(() => import('./components/BlogPage'));
const BlogPostPage = lazy(() => import('./components/BlogPostPage'));
const ScrimmageHub = lazy(() => import('./components/ScrimmageHub'));
const ClubPage = lazy(() => import('./components/ClubPage'));

const LoadingScreen = () => (
  <div className="min-h-screen bg-[#020617] flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
  </div>
);

const App: React.FC = () => {
  const navigate = useNavigate();
  const [showLanguagePicker, setShowLanguagePicker] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(LANGUAGE_PICKED_KEY) !== 'v2') {
      setShowLanguagePicker(true);
    }
  }, []);

  const handleLanguagePicked = (lang: AppLanguage) => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    localStorage.setItem(LANGUAGE_PICKED_KEY, 'v2');
    setShowLanguagePicker(false);
    // Force re-render so all components pick up the new language
    window.dispatchEvent(new Event('ha-language-changed'));
  };

  const handleNavigate = (view: ViewState | 'marketing') => {
    const pathMap: Partial<Record<ViewState | 'marketing', string>> = {
      marketing: '/',
      about: '/about',
      privacy: '/privacy',
      'subscription-terms': '/subscription-terms',
      'match-archive': '/matches',
      'data-erasure': '/data-erasure',
      unsubscribe: '/unsubscribe',
    };
    const path = pathMap[view];
    if (path) {
      navigate(path);
    } else {
      window.location.href = APP_URL;
    }
  };

  const wrap = (children: React.ReactNode) => (
    <div className="px-4 max-w-5xl mx-auto pt-8 pb-24">{children}</div>
  );

  return (
    <div className="min-h-screen bg-[#020617] text-white">
      <LanguagePicker show={showLanguagePicker} onSelect={handleLanguagePicked} />
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/" element={<MarketingHome onNavigate={handleNavigate} isLoggedIn={false} />} />
          <Route path="/about" element={wrap(<AboutPage onBack={() => navigate('/')} />)} />
          <Route path="/privacy" element={wrap(<PrivacyPolicy onBack={() => navigate('/')} />)} />
          <Route path="/subscription-terms" element={wrap(<SubscriptionTerms onBack={() => navigate('/')} />)} />
          <Route path="/matches" element={wrap(<MatchArchive onNavigate={handleNavigate} onBack={() => navigate('/')} userProfile={null} />)} />
          <Route path="/match-viewer/:streamId" element={<MatchViewerWrapper />} />
          <Route path="/data-erasure" element={wrap(<AccountDeletion onBack={() => navigate('/')} />)} />
          <Route path="/unsubscribe" element={wrap(<UnsubscribeView onBack={() => navigate('/')} />)} />
          <Route path="/basketball-plays" element={<BasketballPlaysPage />} />
          <Route path="/basketball-plays/offense" element={<PlayCategoryPage />} />
          <Route path="/basketball-plays/defense" element={<PlayCategoryPage />} />
          <Route path="/basketball-plays/inbounds" element={<PlayCategoryPage />} />
          <Route path="/basketball-plays/:slug" element={<PlayDetailPage />} />
          <Route path="/coach-board" element={<CoachBoardPage />} />
          <Route path="/ai-basketball-coach" element={<AICoachPage />} />
          <Route path="/basketball-playbook-creator" element={<PlaybookCreatorPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/scrimmage-hub" element={<ScrimmageHub user={null} userProfile={null} onBack={() => navigate('/')} />} />
          <Route path="/club" element={wrap(<ClubPage />)} />
          <Route path="/partners" element={<PartnersRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
};

const PartnersRedirect = () => {
  React.useEffect(() => {
    window.location.replace('https://app.hoopsatlas.com/partners');
  }, []);
  return null;
};

const MatchViewerWrapper = () => {
  const { streamId } = useParams<{ streamId: string }>();
  const navigate = useNavigate();
  if (!streamId) return <Navigate to="/matches" replace />;
  return (
    <div className="px-4 max-w-5xl mx-auto">
      <MatchViewer streamId={streamId} onBack={() => navigate('/matches')} />
    </div>
  );
};

export default App;
